'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  CaretRight,
  Hourglass,
  CalendarBlank,
  Clock,
  VideoCamera,
  PlayCircle,
} from '@phosphor-icons/react';
import { NavBarMain } from '@/components/seasons/NavBarMain';
import { BlogShareInline } from '@/components/seasons/BlogShareInline';
import { Footer } from '@/components/seasons/Footer';
import { FlowNavBar } from '@/components/seasons/FlowNavBar';
import { FlowFooter } from '@/components/seasons/FlowFooter';
import { CometNavBar } from '@/components/seasons/CometNavBar';
import { CometFooter } from '@/components/seasons/CometFooter';
import { GalaxyNavBar } from '@/components/seasons/GalaxyNavBar';
import { GalaxyFooter } from '@/components/seasons/GalaxyFooter';
import { GravityNavBar } from '@/components/seasons/GravityNavBar';
import { GravityFooter } from '@/components/seasons/GravityFooter';
import { StoryhealthNavBar } from '@/components/seasons/StoryhealthNavBar';
import { StoryhealthFooter } from '@/components/seasons/StoryhealthFooter';
import { WebinarRegistrationForm } from '@/components/seasons/WebinarRegistrationForm';
import { WebinarVideoPlayer } from '@/components/seasons/WebinarVideoPlayer';
import { WebinarCountdown } from '@/components/seasons/WebinarCountdown';
import type { WebinarFull, WebinarSummary } from '@/lib/webinars';
import { stripHtml } from '@/lib/strip-html';

// ── Figma Seasons gradient lookup ─────────────────────────────────────────────
const FIGMA_GRADIENTS: Record<string, string> = {
  'winter-night':  'linear-gradient(0deg,#000a1b 0%,#053bee 100%)',
  'night-sky':     'linear-gradient(0deg,#000a1b 0%,#a0cce0 100%)',
  'ember-night':   'linear-gradient(90deg,#000a1b 0%,#eb5324 100%)',
  'dusk-glow':     'linear-gradient(0deg,#000a1b 0%,#ffcc9c 100%)',
  'deep-forest':   'linear-gradient(0deg,#000a1b 0%,#05604f 100%)',
  'moonlit-grove': 'linear-gradient(0deg,#000a1b 0%,#aad097 100%)',
  'night-bloom':   'linear-gradient(0deg,#000a1b 0%,#58364e 100%)',
  'late-frost':    'linear-gradient(0deg,#000a1b 0%,#ffadd9 100%)',
  'golden-hour':   'linear-gradient(0deg,#000a1b 0%,#ffbf00 100%)',
  'lantern-light': 'linear-gradient(0deg,#000a1b 0%,#ffe08a 100%)',
  'horizon':       'linear-gradient(0deg,#000a1b 0%,#f0f0f0 100%)',
  'open-sky':      'linear-gradient(0deg,#053bee 0%,#a0cce0 100%)',
  'summer-storm':  'linear-gradient(0deg,#053bee 0%,#eb5324 100%)',
  'dawn-break':    'linear-gradient(0deg,#053bee 0%,#ffcc9c 100%)',
  'deep-current':  'linear-gradient(0deg,#053bee 0%,#05604f 100%)',
  'spring-rain':   'linear-gradient(0deg,#053bee 0%,#aad097 100%)',
  'indigo-dusk':   'linear-gradient(0deg,#053bee 0%,#58364e 100%)',
  'april-shower':  'linear-gradient(0deg,#053bee 0%,#ffadd9 100%)',
  'equinox':       'linear-gradient(0deg,#053bee 0%,#ffbf00 100%)',
  'bright-spark':  'linear-gradient(0deg,#053bee 0%,#ffe08a 100%)',
  'first-frost':   'linear-gradient(0deg,#053bee 0%,#f0f0f0 100%)',
  'coastal':       'linear-gradient(0deg,#a0cce0 0%,#05604f 100%)',
  'fresh-air':     'linear-gradient(180deg,#a0cce0 0%,#aad097 100%)',
  'cloud-wash':    'linear-gradient(0deg,#a0cce0 0%,#f0f0f0 100%)',
  'warm-ember':    'linear-gradient(0deg,#eb5324 0%,#ffcc9c 100%)',
  'autumn-field':  'linear-gradient(0deg,#eb5324 0%,#aad097 100%)',
  'morning-dew':   'linear-gradient(90deg,#aad097 0%,#ffe08a 100%)',
  'vanilla':       'linear-gradient(0deg,#ffcc9c 0%,#f0f0f0 100%)',
};

function bannerGradientStyle(name?: string): React.CSSProperties | undefined {
  const g = name ? FIGMA_GRADIENTS[name] : undefined;
  return g ? { '--wbn-gradient': g } as React.CSSProperties : undefined;
}

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE_OUT } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

// Formats a date-only ISO string (YYYY-MM-DD) or free-form string to "Month D, YYYY"
function displayDate(val?: string): string {
  if (!val) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
  if (dateOnly) {
    const d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
  }
  return val;
}

// Returns the best available ISO-parseable date string for the countdown timer.
function getCountdownDate(airsAt?: string, datetime?: string): string | undefined {
  function tryParse(val: string): Date | null {
    const cleaned = val
      .replace(/\|/g, ' ')
      .replace(/\bat\b/gi, ' ')
      .replace(/\b(E[DS]?T|C[DS]?T|M[DS]?T|P[DS]?T)\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const d = new Date(cleaned);
    return isNaN(d.getTime()) ? null : d;
  }
  if (airsAt) {
    const d = tryParse(airsAt);
    if (d) return d.toISOString();
  }
  if (datetime) {
    const d = tryParse(datetime);
    if (d) return d.toISOString();
  }
  return undefined;
}

// Formats an ISO datetime string to "Month D, YYYY · H:MM AM/PM ET"
function displayDatetime(val?: string): string {
  if (!val) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
  if (dateOnly) {
    const d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
    return `${date} · ${time} ET`;
  }
  return val;
}

// Displays duration — appends "min" only when value is a plain number
function displayDuration(val?: string | number): string {
  if (val === undefined || val === null || val === '') return '';
  const s = String(val).trim();
  if (/^\d+$/.test(s)) return `${s} min`;
  return s;
}

// Extracts the date portion from an airsAt value as stored in the CMS
function extractAirsAtDate(val?: string): string {
  if (!val) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
  if (dateOnly) {
    const d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const iso = new Date(val);
  if (!isNaN(iso.getTime())) {
    return iso.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
  }
  if (val.includes('|')) return val.split('|')[0].trim();
  const m = val.match(/^([A-Za-z]+ \d{1,2},?\s+\d{4})/);
  return m ? m[1].trim() : val;
}

// Extracts the time portion from an airsAt value as stored in the CMS
function extractAirsAtTime(val?: string): string {
  if (!val) return '';
  if (/^(\d{4})-(\d{2})-(\d{2})$/.test(val)) return '';
  const iso = new Date(val);
  if (!isNaN(iso.getTime())) {
    return iso.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET';
  }
  if (val.includes('|')) return val.split('|')[1]?.trim() ?? '';
  const m = val.match(/[A-Za-z]+ \d{1,2},?\s+\d{4}\s+(.+)/);
  return m ? m[1].trim() : '';
}

interface Props {
  webinar: WebinarFull;
  related: WebinarSummary[];
  urlBrand?: string;
  /** Read server-side in page.tsx from the request headers — see getClientIp. */
  visitorIp?: string;
}

export default function WebinarDetailClient({ webinar, related, urlBrand, visitorIp }: Props) {
  const isUpcoming = webinar.status === 'upcoming';
  const brand = (urlBrand ?? '') || ((webinar as { brand?: string }).brand ?? '').toLowerCase();
  const isFlow   = brand === 'flow';
  const isComet  = brand === 'comet';
  const isGalaxy = brand === 'galaxy';
  const isGravity = brand === 'gravity';
  const isStoryhealth = brand === 'storyhealth';
  const webinarBase = isFlow ? '/flow/resources/webinars' : isComet ? '/comet/resources/webinars' : isGalaxy ? '/galaxy/resources/webinars' : isStoryhealth ? '/storyhealth/resources/webinars' : '/resources/webinars';
  return (
    <div className="webinars-page" style={{ background: 'var(--seasons-color-surface-default)' }}>
      {isFlow    ? <FlowNavBar />
       : isComet  ? <CometNavBar />
       : isGalaxy ? <GalaxyNavBar />
       : isGravity ? <GravityNavBar />
       : isStoryhealth ? <StoryhealthNavBar />
       : <NavBarMain ctaLabel="REQUEST A DEMO" ctaHref="https://innovaccer.com/request-a-demo" storiesHref="/blogs" />}

      <main>
        {isUpcoming ? (
          /* ── Upcoming: gradient-banner layout ────────────────────────────── */
          <section className="wbn-upcoming section-x-pad">
            <motion.div initial="hidden" animate="visible" variants={stagger} className="wbn-upcoming__inner">

              {/* Breadcrumb */}
              <motion.div variants={fadeUp} className="wbn-od-breadcrumb">
                <Link href={webinarBase} className="wbn-od-breadcrumb__link">Webinars</Link>
                <CaretRight size={12} weight="bold" aria-hidden="true" style={{ color: 'var(--seasons-color-text-tertiary)' }} />
                <Link href={`${webinarBase}?tab=Upcoming`} className="wbn-od-breadcrumb__link">Upcoming</Link>
              </motion.div>

              {/* Pill */}
              <motion.div variants={fadeUp}>
                <span className="wbn-upcoming__pill">
                  <VideoCamera size={11} weight="fill" aria-hidden="true" />
                  Live webinar
                </span>
              </motion.div>

              {/* H1 title — rich text (bold/italic/link/sup/sub), stored as sanitized HTML */}
              <motion.h1 variants={fadeUp} className="type-display-lg wbn-upcoming__title" dangerouslySetInnerHTML={{ __html: webinar.title || '' }} />

              {/* Meta strip: date · time · duration */}
              <motion.div variants={fadeUp} className="wbn-od-meta wbn-upcoming__meta">
                {webinar.datetime ? (
                  <span className="wbn-od-meta__item">
                    <CalendarBlank size={14} weight="regular" aria-hidden="true" />
                    {webinar.datetime}
                  </span>
                ) : (
                  <>
                    {webinar.airsAt && extractAirsAtDate(webinar.airsAt) && (
                      <span className="wbn-od-meta__item">
                        <CalendarBlank size={14} weight="regular" aria-hidden="true" />
                        {extractAirsAtDate(webinar.airsAt)}
                      </span>
                    )}
                    {webinar.airsAt && extractAirsAtTime(webinar.airsAt) && (
                      <span className="wbn-od-meta__item">
                        <Clock size={14} weight="regular" aria-hidden="true" />
                        {extractAirsAtTime(webinar.airsAt)}
                      </span>
                    )}
                  </>
                )}
                {webinar.durationMinutes !== undefined && (
                  <span className="wbn-od-meta__item">
                    <Hourglass size={14} weight="regular" aria-hidden="true" />
                    {displayDuration(webinar.durationMinutes)}
                  </span>
                )}
              </motion.div>

              {/* Gradient banner: thumbnail left / form card right */}
              <motion.div variants={fadeUp} className="wbn-upcoming__banner" style={bannerGradientStyle(webinar.bannerGradient)}>
                <div className="wbn-upcoming__thumb-card">
                  {webinar.heroImg ? (
                    <img src={webinar.heroImg} alt="" className="wbn-upcoming__thumb-img" />
                  ) : (
                    <div className="wbn-upcoming__thumb-fallback" aria-hidden="true" />
                  )}
                </div>
                <div className="wbn-upcoming__form-card">
                  <div className="wbn-upcoming__form-hd">
                    <h2 className="wbn-upcoming__form-heading">Register for this webinar</h2>
                    <p className="wbn-upcoming__form-sub">Fill in the form below to save your spot.</p>
                  </div>
                  <WebinarRegistrationForm
                    slug={webinar.slug}
                    webinarTitle={stripHtml(webinar.title || '')}
                    hideHeading
                    airsAt={webinar.airsAt}
                    durationMinutes={webinar.durationMinutes}
                    dek={webinar.dek}
                    visitorIp={visitorIp}
                  />
                </div>
              </motion.div>

              {/* Countdown strip — uses airsAt if set, else falls back to datetime field */}
              {(() => {
                const countdownDate = getCountdownDate(webinar.airsAt, webinar.datetime);
                return countdownDate ? (
                  <motion.div variants={fadeUp} className="wbn-upcoming__countdown-wrap">
                    <WebinarCountdown airsAt={countdownDate} variant="minimal" />
                  </motion.div>
                ) : null;
              })()}

            </motion.div>
          </section>
        ) : (
          /* ── On-demand: same structure as upcoming ───────────────────────── */
          <section className="wbn-upcoming section-x-pad">
            <motion.div initial="hidden" animate="visible" variants={stagger} className="wbn-upcoming__inner">

              {/* Breadcrumb */}
              <motion.div variants={fadeUp} className="wbn-od-breadcrumb">
                <Link href={webinarBase} className="wbn-od-breadcrumb__link">Webinars</Link>
                <CaretRight size={12} weight="bold" aria-hidden="true" style={{ color: 'var(--seasons-color-text-tertiary)' }} />
                <Link href={`${webinarBase}?tab=On+Demand`} className="wbn-od-breadcrumb__link">On Demand</Link>
              </motion.div>

              {/* Pill */}
              <motion.div variants={fadeUp}>
                <span className="wbn-upcoming__pill">
                  <PlayCircle size={11} weight="fill" aria-hidden="true" />
                  On Demand
                </span>
              </motion.div>

              {/* H1 title — rich text (bold/italic/link/sup/sub), stored as sanitized HTML */}
              <motion.h1 variants={fadeUp} className="type-display-lg wbn-upcoming__title" dangerouslySetInnerHTML={{ __html: webinar.title || '' }} />

              {/* Meta strip: date recorded · duration */}
              {(webinar.datetime || webinar.originallyAired || webinar.durationMinutes) && (
                <motion.div variants={fadeUp} className="wbn-od-meta wbn-upcoming__meta">
                  {webinar.datetime ? (
                    <span className="wbn-od-meta__item">
                      <CalendarBlank size={14} weight="regular" aria-hidden="true" />
                      {webinar.datetime}
                    </span>
                  ) : webinar.originallyAired && (
                    <span className="wbn-od-meta__item">
                      <CalendarBlank size={14} weight="regular" aria-hidden="true" />
                      {displayDate(webinar.originallyAired)}
                    </span>
                  )}
                  {webinar.durationMinutes !== undefined && (
                    <span className="wbn-od-meta__item">
                      <Hourglass size={14} weight="regular" aria-hidden="true" />
                      {displayDuration(webinar.durationMinutes)}
                    </span>
                  )}
                </motion.div>
              )}

              {/* Banner — same wrapper as upcoming, video player inside instead of thumb+form */}
              <motion.div
                variants={fadeUp}
                className="wbn-upcoming__banner wbn-upcoming__banner--player"
                style={bannerGradientStyle(webinar.bannerGradient)}
              >
                <WebinarVideoPlayer
                  slug={webinar.slug}
                  webinarTitle={stripHtml(webinar.title || '')}
                  videoUrl={webinar.videoUrl}
                  heroImg={webinar.heroImg}
                  speakers={webinar.speakers}
                  originallyAired={webinar.originallyAired}
                  durationMinutes={webinar.durationMinutes !== undefined ? (typeof webinar.durationMinutes === 'number' ? webinar.durationMinutes : parseFloat(String(webinar.durationMinutes)) || undefined) : undefined}
                  transcript={webinar.transcript}
                  bannerGradient={webinar.bannerGradient}
                  visitorIp={visitorIp}
                />
              </motion.div>

            </motion.div>
          </section>
        )}

        {/* ── Agenda (upcoming only) ──────────────────────────────────────── */}
        {isUpcoming && webinar.agenda && webinar.agenda.length > 0 && (
          <SectionBlock heading="Agenda" headingClass="wbn-section__heading--sub">
            <AgendaList items={webinar.agenda} />
          </SectionBlock>
        )}

        {/* ── About this webinar ──────────────────────────────────────────── */}
        {webinar.body.trim().length > 0 && (
          <SectionBlock
            heading="About this webinar"
            headingClass="wbn-section__heading--sub"
            rightAction={
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--seasons-space-3)' }}>
                <span className="wbn-share-label">Share</span>
                <BlogShareInline title={stripHtml(webinar.title || '')} />
              </div>
            }
          >
            <div className="wbn-prose">
              {webinar.body
                .split(/\n\n+/)
                .map((p) => p.trim())
                .filter(Boolean)
                .map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                ))}
            </div>
          </SectionBlock>
        )}

        {/* ── What you'll learn (upcoming) ────────────────────────────────── */}
        {isUpcoming && webinar.learnings && webinar.learnings.length > 0 && (
          <SectionBlock heading="What you'll learn" headingClass="wbn-section__heading--sub">
            <ul className="wbn-learn-blocks">
              {webinar.learnings.map((item, i) => (
                <li key={i} className="wbn-learn-blocks__item">{item}</li>
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* ── Key takeaways (on-demand) ───────────────────────────────────── */}
        {!isUpcoming && webinar.takeaways && webinar.takeaways.length > 0 && (
          <SectionBlock heading="What you'll take away" headingClass="wbn-section__heading--sub">
            <ul className="wbn-learn-blocks">
              {webinar.takeaways.map((item, i) => (
                <li key={i} className="wbn-learn-blocks__item">{item}</li>
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* ── Who should attend ─────────────────────────────────────────── */}
        {webinar.whoShouldAttend && (
          <SectionBlock
            heading="Who should attend"
            headingClass="wbn-section__heading--sub"
            className="wbn-section--who-attend"
          >
            <p className="wbn-prose-single">{webinar.whoShouldAttend}</p>
            {webinar.topics && webinar.topics.length > 0 && (
              <div className="wbn-topic-tags" style={{ marginTop: 'var(--seasons-space-8)' }}>
                <span className="wbn-share-label">Topics</span>
                {webinar.topics.map((topic) => (
                  <a
                    key={topic}
                    href={`${webinarBase}?q=${encodeURIComponent(topic)}`}
                    className="blog-tag blog-tag--muted"
                  >
                    {topic}
                  </a>
                ))}
              </div>
            )}
          </SectionBlock>
        )}

        {/* ── Topics (fallback when no whoShouldAttend section) ──────────── */}
        {!webinar.whoShouldAttend && webinar.topics && webinar.topics.length > 0 && (
          <section className="section-x-pad wbn-share-topics">
            <div className="wbn-section__inner">
              <div className="wbn-topic-tags">
                <span className="wbn-share-label">Topics</span>
                {webinar.topics.map((topic) => (
                  <a key={topic} href={`${webinarBase}?q=${encodeURIComponent(topic)}`} className="blog-tag blog-tag--muted">
                    {topic}
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Speakers ───────────────────────────────────────────────────── */}
        {webinar.speakers && webinar.speakers.length > 0 && (
          <SectionBlock
            heading="Who you'll hear from"
            className="wbn-section--limestone"
            headingClass="wbn-section__heading--sub"
          >
            <SpeakerV6Grid speakers={webinar.speakers} gradient={true} />
          </SectionBlock>
        )}

        {/* ── Related webinars ───────────────────────────────────────────── */}
        {related.length > 0 && (
          <section className="section-x-pad wbn-more">
            <div className="wbn-section__inner">
            <div className="wbn-more__header">
              <h2 className="type-display-md wbn-more__heading">More webinars</h2>
              <Link href={webinarBase} className="block-button-link wbn-more__view-all">
                <span className="block-button block-button--md block-button--outline">
                  <span className="block-button__text">
                    <span className="btn-primary-text">View all webinars</span>
                    <span className="btn-hover-text">View all webinars</span>
                  </span>
                </span>
              </Link>
            </div>
            <div className="wbn-more__grid">
              {related.map((w) => (
                <Link key={w.slug} href={`${webinarBase}/${w.slug}`} className="wbn-more__card">
                  <div className="wbn-more__media">
                    {w.heroImg ? (
                      <img src={w.heroImg} alt="" loading="lazy" />
                    ) : (
                      <div
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(135deg, var(--seasons-color-limestone) 0%, var(--seasons-color-cyber-blue-100) 100%)',
                        }}
                      />
                    )}
                  </div>
                  <div className="wbn-more__body">
                    <h3 className="type-heading-md wbn-more__title">{w.title}</h3>
                    {w.dek && <p className="type-body-md wbn-more__dek">{w.dek}</p>}
                  </div>
                  <div className="wbn-more__meta">
                    <span>{w.status === 'upcoming' ? 'Live webinar' : 'On Demand'}</span>
                    <span className="bl-sep" aria-hidden="true" />
                    <span>
                      {w.status === 'upcoming'
                        ? displayDate(w.airsAt)
                        : displayDate(w.originallyAired)}
                    </span>
                    {w.durationMinutes && (
                      <>
                        <span className="bl-sep" aria-hidden="true" />
                        <span>{displayDuration(w.durationMinutes)}</span>
                      </>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            </div>
          </section>
        )}
      </main>

      {isFlow    ? <FlowFooter />
       : isComet  ? <CometFooter />
       : isGalaxy ? <GalaxyFooter />
       : isGravity ? <GravityFooter />
       : isStoryhealth ? <StoryhealthFooter />
       : <Footer theme="light" />}
    </div>
  );
}

// ── Reusable section wrapper ───────────────────────────────────────────────

function SectionBlock({
  eyebrow,
  heading,
  children,
  className,
  headingClass = 'type-display-md',
  rightAction,
}: {
  eyebrow?: string;
  heading: string;
  children: React.ReactNode;
  className?: string;
  headingClass?: string;
  rightAction?: React.ReactNode;
}) {
  return (
    <motion.section
      className={`section-x-pad wbn-section${className ? ` ${className}` : ''}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      variants={stagger}
    >
      <div className="wbn-section__inner">
        {eyebrow && (
          <motion.div variants={fadeUp} className="wbn-section__eyebrow">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
              <circle cx="8" cy="8" r="7" stroke="var(--seasons-color-cyber-blue)" strokeOpacity="0.25" strokeWidth="1.5" />
              <circle cx="8" cy="8" r="4" fill="var(--seasons-color-cyber-blue)" />
            </svg>
            <p className="type-eyebrow-lg" style={{ color: 'var(--seasons-color-text-brand)' }}>{eyebrow}</p>
          </motion.div>
        )}
        <motion.div variants={fadeUp} className="wbn-section__heading-row">
          <h2 className={`${headingClass} wbn-section__heading`}>{heading}</h2>
          {rightAction && <div className="wbn-section__heading-action">{rightAction}</div>}
        </motion.div>
        <motion.div variants={fadeUp}>{children}</motion.div>
      </div>
    </motion.section>
  );
}

// ── Agenda list ────────────────────────────────────────────────────────────

function AgendaList({ items }: { items: Array<{ time: string; title: string; description?: string }> }) {
  return (
    <ol className="wbn-agenda">
      {items.map((item, i) => (
        <li key={i} className="wbn-agenda__item">
          <span className="wbn-agenda__time">{item.time}</span>
          <div className="wbn-agenda__body">
            <p className="wbn-agenda__title">{item.title}</p>
            {item.description && <p className="wbn-agenda__desc">{item.description}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── Speaker grid ───────────────────────────────────────────────────────────

interface Speaker { name: string; role: string; photo?: string; bio?: string }

function splitRole(role: string) {
  const i = role.indexOf(', ');
  return i === -1
    ? { title: role, org: '' }
    : { title: role.slice(0, i), org: role.slice(i + 2) };
}

function SpeakerV6Grid({ speakers, gradient = false }: { speakers: Speaker[]; gradient?: boolean }) {
  return (
    <div className="wbn-speakers-grid">
      {speakers.map((s, i) => {
        const { title, org } = splitRole(s.role);
        return (
          <div key={i} className={`wbn-speaker-card--v6${gradient ? ' wbn-speaker-card--gradient' : ''}`}>
            <div className={gradient ? 'wbn-sc__photo-wrap' : undefined}>
              {s.photo && <img src={s.photo} alt="" className="wbn-sc__photo" />}
            </div>
            <div className="wbn-sc__info">
              <h3 className="wbn-sc__name">{s.name}</h3>
              {org && <p className="wbn-sc__org">{org}</p>}
              {title && <p className="wbn-sc__role">{title}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
